import { notificationsState, tokenState } from 'atoms/general';
import React, { useState, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import { mapAPIKey, PUBLIC_URL } from '../configs/config';
import URL from '../configs/URL';
import fetchWithRetry from '../utils/fetch';

import type { RoleResponse, Role } from 'types/user';
import useMQTT from 'hooks/useMQTT';

declare global {
  interface Window {
    userPermissions: any;
    userRole: any;
  }
}

interface UserData {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ContextProps {
  userData: UserData | undefined;
  setUserData: React.Dispatch<React.SetStateAction<UserData | undefined>>;
  isLoggedin: boolean;
  permissions: any | undefined;
  myPermissions: Record<string, any>[] | undefined | null;
  setMyPermissions: React.Dispatch<
    React.SetStateAction<Record<string, any>[] | null | undefined>
  >;
  roles: Role[] | undefined;
}

const UserContext = React.createContext<Partial<ContextProps>>({});

function UserProvider(props: any) {
  const token = useRecoilValue(tokenState);
  const setNotifications = useSetRecoilState(notificationsState);

  const [userData, setUserData] = useState<UserData>();
  const [permissions, setPermissions] = useState<any>();
  const [myPermissions, setMyPermissions] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, any>[] | undefined | null
  >(null);

  const allowPagesRole = ['/login', '/share', '/web'];

  useMQTT({
    host: 'dev.map.ir',
    port: 443,
    userID: userData?.id,
    onMessageArrived(message) {
      setNotifications(message);
    },
  });

  const { data: roles } = useSWR(
    !allowPagesRole.some((p) =>
      (PUBLIC_URL + window.location.pathname).startsWith(p)
    ) &&
      ['admin', 'manager', 'ادمین', 'مدیر'].includes(
        (userData as any)?.role ?? ''
      )
      ? `${URL.roles}`
      : null,
    (url: string) =>
      fetchWithRetry(url, {
        method: 'GET',
        headers: {
          token: token ?? '',
          'x-api-key': `${mapAPIKey}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res: Response | undefined) => res?.json())
        .then((res: RoleResponse) => res.value),
    {
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      focusThrottleInterval: 10000,
    }
  );

  useEffect(() => {
    window.userPermissions = permissions;
  }, [permissions]);

  useEffect(() => {
    if (myPermissions) {
      const permissionTemplate = myPermissions?.map((c) => ({
        domain: c.domain,
        action: c.action,
        access: c.access,
        subdomain: c.subdomain,
        identifier: c.identifier,
        title: c.title,
        service: c.service,
      }));

      let _permissions: any = permissionTemplate?.reduce(
        (acc: any, cur: any) => {
          const { domain, action, access, subdomain, identifier, service } =
            cur;

          acc[domain ?? service] = {
            ...(acc[domain ?? service] ?? {}),
          };

          if (identifier && !subdomain) {
            acc[domain ?? service]['_' + identifier] = {
              ...(acc[domain ?? service]['_' + identifier] ?? {}),
            };
            acc[domain ?? service]['_' + identifier][action] = access;
          } else if (subdomain && !identifier) {
            acc[domain ?? service]['_' + subdomain] = {
              ...(acc[domain ?? service]['_' + subdomain] ?? {}),
            };
            acc[domain ?? service]['_' + subdomain][action] = access;
          } else if (subdomain && identifier) {
            acc[domain ?? service]['_' + subdomain]['_' + identifier] = {
              ...(acc[domain ?? service]['_' + subdomain]['_' + identifier] =
                {
                  ...(acc[domain ?? service]['_' + subdomain][
                    '_' + identifier
                  ] ?? {}),
                } ?? {}),
            };
            acc[domain ?? service]['_' + subdomain]['_' + identifier][action] =
              access;
          } else if (!subdomain && !identifier) {
            acc[domain ?? service][action] = access;
          }

          return acc;
        }
      );

      /// clean up previous step.
      if (_permissions?.domain || _permissions?.service) {
        if (!_permissions?.[_permissions?.domain || _permissions?.service]) {
          _permissions = {
            ..._permissions,
            [_permissions?.domain || _permissions?.service]: {},
          };
        }

        if (_permissions?.identifier && !_permissions?.subdomain) {
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.identifier
          ] = {
            ...(_permissions[_permissions?.domain ?? _permissions?.service][
              '_' + _permissions?.identifier
            ] ?? {}),
          };
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.identifier
          ][_permissions?.action] = _permissions?.access;
        } else if (_permissions?.subdomain && !_permissions?.identifier) {
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.subdomain
          ] = {
            ...(_permissions[_permissions?.domain ?? _permissions?.service][
              '_' + _permissions?.subdomain
            ] ?? {}),
          };
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.subdomain
          ][_permissions?.action] = _permissions?.access;
        } else if (_permissions?.subdomain && _permissions?.identifier) {
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.subdomain
          ]['_' + _permissions?.identifier] = {
            ...(_permissions[_permissions?.domain ?? _permissions?.service][
              '_' + _permissions?.subdomain
            ]['_' + _permissions?.identifier] =
              {
                ...(_permissions[_permissions?.domain ?? _permissions?.service][
                  '_' + _permissions?.subdomain
                ]['_' + _permissions?.identifier] ?? {}),
              } ?? {}),
          };
          _permissions[_permissions?.domain ?? _permissions?.service][
            '_' + _permissions?.subdomain
          ]['_' + _permissions?.identifier][_permissions?.action] =
            _permissions?.access;
        } else if (!_permissions?.subdomain && !_permissions?.identifier) {
          _permissions[_permissions?.domain ?? _permissions?.service][
            _permissions?.action
          ] = _permissions?.access;
        }
        delete _permissions?.domain;
        delete _permissions?.subdomain;
        delete _permissions?.access;
        delete _permissions?.action;
        delete _permissions?.identifier;
        delete _permissions?.service;
        delete _permissions?.title;
      }

      setPermissions(_permissions);
    }
  }, [myPermissions]);

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        roles,
        permissions,
        myPermissions,
        setMyPermissions,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}

export { UserContext, UserProvider };
